import { useRef, useState, type KeyboardEvent } from 'react';

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Centering wrapper so the composer aligns with the chat column on desktop. */
  contentClass?: string;
}

export function InputBar({ onSend, disabled, placeholder, contentClass = 'w-full' }: Props) {
  const [value, setValue] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    if (taRef.current) taRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const autoGrow = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 96)}px`;
  };

  const canSend = value.trim().length > 0 && !disabled;

  return (
    <div className="shrink-0 border-t border-border bg-white">
      <div className={`flex min-h-[72px] items-end gap-2 px-3 py-3 ${contentClass}`}>
      <div className="flex flex-1 items-end rounded-lg bg-surface px-3 py-2 ring-1 ring-border transition-shadow focus-within:ring-2 focus-within:ring-[var(--brand-accent)]">
        <textarea
          ref={taRef}
          rows={1}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            autoGrow();
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder ?? 'Type a message…'}
          className="max-h-24 w-full resize-none bg-transparent text-sm leading-relaxed text-primary placeholder:text-mid/60 focus:outline-none disabled:opacity-60"
        />
      </div>
      <button
        onClick={submit}
        disabled={!canSend}
        aria-label="Send message"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white shadow-card transition-all active:scale-90 disabled:cursor-not-allowed disabled:opacity-40"
        style={{ backgroundColor: 'var(--brand-accent)' }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      </button>
      </div>
    </div>
  );
}

export default InputBar;
